import { Suspense, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ChevronsRight } from "lucide-react";

import { Button, RouteFallback, Sheet, SheetContent, SheetTitle } from "@/shared/ui";
import { normalizeSource } from "@/lib/authHelpers";
import { useSession } from "@/lib/session";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/shared/lib/cn";
import type { Source } from "@/types";

import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { menuIdForPath } from "./nav";
import { useIdleRoutePrefetch } from "./routePrefetch";
import { useLoginContext } from "./useLoginContext";
import { useMenuVisibility } from "./useMenuVisibility";
import { useSyncReduxAuth } from "./useSyncReduxAuth";
import { SidebarToggleProvider, useSidebarToggle } from "./SidebarToggleContext";

/**
 * Application shell — codonix "PLX new-version" layout: a CSS grid where the
 * sidebar column animates from `--sidebar-width` to `0px` (the rail slides
 * fully off-screen rather than shrinking to icons), with a floating re-opener
 * on the left edge when hidden. Header spans the full width; the content
 * region uses the `bg-muted/50` wash so cards read as elevated surfaces
 * (30% was imperceptible on most displays — muted is 96% lightness).
 * Guards the authenticated area and provides a mobile navigation drawer.
 */
export function AppShell() {
  return (
    <SidebarToggleProvider>
      <AppShellInner />
    </SidebarToggleProvider>
  );
}

function AppShellInner() {
  const { data: session, status } = useSession();
  const { collapsed, setCollapsed, toggle } = useSidebarToggle();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile(768);
  const pathname = useLocation().pathname;

  useSyncReduxAuth(session);
  const { context, resolved } = useLoginContext(status === "authenticated" && !!session);
  const menu = useMenuVisibility(resolved);
  // Warm the nav route chunks while the user reads the first screen, so
  // every sidebar click after login renders without a chunk fetch.
  useIdleRoutePrefetch();

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // An account whose password was typed by whoever created it must replace it
  // before anything else — in practice one password gets reused across a batch
  // of colleagues, and the server refuses all user administration until it is
  // changed. /reset-password sits outside this shell, so this cannot loop.
  if (context?.requirePasswordChange) {
    return <Navigate to="/reset-password" replace />;
  }

  const scope = normalizeSource(session.user.scope) as Source | undefined;
  const isAgent = session.user.isAgent === true;

  // The allowlist has to hold for NAVIGATION too. Hiding a rail entry never
  // stopped anyone opening the URL, so a seated admin could still read /claims
  // or /reports by typing the address.
  const routeMenuId = menuIdForPath(pathname);
  if (menu.backendResolved && routeMenuId && !menu.isAllowed(routeMenuId)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // The member dashboard (`/dashboard/:id`) is a full-bleed surface with no rail.
  const isMemberDashboard = /^\/dashboard\/[^/]+/.test(pathname);
  const railHidden = collapsed || isMobile || isMemberDashboard;

  return (
    <div
      className="grid h-screen grid-rows-[var(--header-height)_1fr] overflow-hidden bg-background text-foreground transition-[grid-template-columns] duration-300 ease-in-out"
      style={{ gridTemplateColumns: railHidden ? "0px 1fr" : "var(--sidebar-width) 1fr" }}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Header spans the full width */}
      <header className="col-span-2 row-start-1 z-40">
        <Topbar
          scope={scope}
          user={session.user}
          isMobile={isMobile}
          onToggle={isMobile ? () => setMobileOpen(true) : toggle}
        />
      </header>

      {/* Sidebar column — animates to 0px when hidden; inner wrapper keeps a
          fixed width so contents don't reflow during the slide. */}
      <aside
        className={cn(
          "row-start-2 col-start-1 h-full w-full overflow-hidden border-r bg-card transition-[border-color] duration-300",
          railHidden ? "border-transparent" : "border-border"
        )}
        aria-hidden={railHidden}
      >
        <div
          className="h-full overflow-y-auto"
          style={{ width: "var(--sidebar-width)" }}
          inert={railHidden || undefined}
        >
          <Sidebar
            scope={scope}
            isAgent={isAgent}
            visibleMenuIds={menu.visibleMenuIds}
            manageableLevels={menu.manageableLevels}
            menuVisibilityKnown={menu.known}
          />
        </div>
      </aside>

      {/* Content area — soft muted wash (codonix) */}
      <main
        id="main"
        tabIndex={-1}
        className="row-start-2 col-start-2 overflow-y-auto bg-muted/50 focus:outline-none"
      >
        <div className="p-4 md:p-6">
          {/* Scoped to the content area on purpose. Hoisted to the router, a
              lazy route chunk suspends the whole tree and the rail and header
              blink out on every first visit to a page. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Floating re-opener — only when the rail is collapsed on desktop. */}
      {collapsed && !isMobile && !isMemberDashboard && (
        <Button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Open sidebar"
          title="Open sidebar"
          className="fixed left-0 top-1/2 z-30 h-16 w-9 -translate-y-1/2 items-center justify-center rounded-l-none rounded-r-lg bg-brand-primary p-0 text-brand-primary-foreground shadow-lg ring-1 ring-brand-primary/40 transition-colors hover:bg-brand-primary/90"
        >
          <ChevronsRight className="h-5 w-5" aria-hidden="true" />
        </Button>
      )}

      {/* Mobile navigation drawer */}
      {isMobile && !isMemberDashboard && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-[min(85vw,18rem)] p-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="h-full overflow-y-auto pt-2">
              <Sidebar
                scope={scope}
                isAgent={isAgent}
                visibleMenuIds={menu.visibleMenuIds}
                manageableLevels={menu.manageableLevels}
                menuVisibilityKnown={menu.known}
                onNavigate={() => setMobileOpen(false)}
                showCollapse={false}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
